import {
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { findNextScrollableParent } from '@components/component-helpers';
import { useIsMobile } from '@hooks/use-is-mobile';
import { debounce, getVisibleHeight } from '@utils/browser';

interface PageContentProps {
  scroll?: boolean;
  alwaysScroll?: boolean;
  noPadding?: boolean;
  bottomGap?: number;
  children: ReactNode;
}

export const StyledPageContent = styled.div<{ $noPadding: boolean; $alwaysScroll: boolean }>(
  (p) => ({
    padding: p.$noPadding ? 0 : p.theme.spacing.m24,
    flexGrow: 1,
    flexShrink: 0,
    height: 'auto',
    overflowY: p.$alwaysScroll ? 'scroll' : 'auto',
    position: 'relative',

    [p.theme.mobileQuery]: {
      padding: p.$noPadding ? 0 : p.theme.spacing.m24,
      overflowY: 'visible',
    },
  })
);

export const PageContent = observer(
  forwardRef((props: PageContentProps, ref?: any) => {
    const { scroll = true, alwaysScroll = true, noPadding = false, bottomGap = 0, ...rest } = props;
    const localRef = useRef(null);
    const intervalRef = useRef<number | undefined>();
    const isMobile = useIsMobile();

    const actualRef = useMemo(() => ref ?? localRef, [ref, localRef]);

    const setHeight = useCallback(() => {
      if (!actualRef.current) return;
      const y = actualRef.current.getBoundingClientRect().top;
      if (y > 0) {
        const height = getVisibleHeight() - y - bottomGap;
        actualRef.current.style.height = height + 'px';
        window.clearInterval(intervalRef.current);
      }
    }, [bottomGap, actualRef]);

    useLayoutEffect(() => {
      if ((!scroll && !alwaysScroll) || isMobile) return;
      intervalRef.current = window.setInterval(() => {
        setHeight();
      }, 16);
    }, [alwaysScroll, scroll, setHeight, isMobile]);

    useEffect(() => {
      const debouncedSetHeight = debounce(setHeight, 200);
      window.addEventListener('resize', debouncedSetHeight);
      return () => window.removeEventListener('resize', debouncedSetHeight);
    }, [setHeight]);

    return (
      <StyledPageContent
        {...rest}
        ref={actualRef}
        $alwaysScroll={alwaysScroll}
        $noPadding={noPadding}
      />
    );
  })
);

export const AutoParentScrollPageContent = observer(({ children }: { children: ReactNode }) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const parentNode = findNextScrollableParent(contentRef.current);
    parentNode?.scrollTo(0, 0);
  }, []);

  return <Content ref={contentRef}>{children}</Content>;
});

const Content = styled(PageContent)((p) => ({
  padding: 0,
  [p.theme.mobileQuery]: {
    overflowX: 'auto',
  },
}));

export const Page = styled.div({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  height: '100%',
  flexGrow: 1,
  overflowX: 'auto',
  overflowY: 'hidden',
  '@media(max-width: 768px)': {
    overflowY: 'visible',
  },
});
